/*
 * WB custom: show only the selected variant's images in the product gallery.
 *
 * How images are grouped (no tagging needed, just the image order in the Shopify admin):
 *   - Each variant has a variant image. That image and every image after it, up to the next
 *     variant image, belong to that variant.
 *   - Images before the first variant image are shared and always show.
 *   - A variant without its own image shows every image.
 *
 * Works for the slider, the thumbnails and the desktop grid layout. Hidden slides are taken out
 * of the slider (and put back later) so shoppers can't swipe into another variant's images.
 * Enabled by the "Show only the selected variant's images" setting in sections/main-product.liquid.
 */
(() => {
  class WbVariantMedia {
    constructor(gallery) {
      this.gallery = gallery;
      const data = JSON.parse(gallery.querySelector('[data-wb-variant-media]').textContent);
      this.variantMedia = data.variants || {};
      this.variantMediaIds = new Set(Object.values(this.variantMedia).filter(Boolean).map(String));

      this.mainWrapper = gallery.querySelector('.main-product__media--slider-wrapper .swiper-wrapper');
      this.thumbsWrapper = gallery.querySelector('swiper-product-thumbs .swiper-wrapper');
      this.mainSlides = this.mainWrapper ? Array.from(this.mainWrapper.children) : [];
      this.thumbSlides = this.thumbsWrapper ? Array.from(this.thumbsWrapper.children) : [];
      this.gridItems = Array.from(gallery.querySelectorAll('.main-product__media--grid-item'));

      // Work out each image's group from the gallery order.
      this.groupOf = {};
      let current = 'shared';
      this.mainSlides.concat(this.gridItems).forEach(element => {
        const id = this.mediaId(element);
        if (!id || this.groupOf[id]) return;
        if (this.variantMediaIds.has(id)) current = id;
        this.groupOf[id] = current;
      });

      this.apply(data.selected);
    }

    mediaId(element) {
      const card = element.matches('[data-media-id]') ? element : element.querySelector('[data-media-id]');
      return card ? String(card.dataset.mediaId) : null;
    }

    isVisible(element, group) {
      if (!group) return true;
      const id = this.mediaId(element);
      if (!id) return true;
      const mediaGroup = this.groupOf[id] || 'shared';
      return mediaGroup === 'shared' || mediaGroup === group;
    }

    apply(variantId) {
      const featured = this.variantMedia[String(variantId)];
      const group = featured ? String(featured) : null;

      if (this.mainWrapper) {
        this.mainWrapper.replaceChildren(...this.mainSlides.filter(slide => this.isVisible(slide, group)));
      }
      if (this.thumbsWrapper) {
        this.thumbsWrapper.replaceChildren(...this.thumbSlides.filter(slide => this.isVisible(slide, group)));
      }
      this.gridItems.forEach(item => item.classList.toggle('hidden', !this.isVisible(item, group)));

      // Refresh the sliders and jump to the variant's image.
      const target = group
        ? Array.from(this.mainWrapper?.children || []).findIndex(slide => this.mediaId(slide) === group)
        : 0;
      [this.gallery.querySelector('swiper-product-thumbs'), this.gallery.querySelector('.main-product__media--slider-wrapper swiper-product-gallery')]
        .forEach(el => {
          const swiper = el?.instance;
          if (!swiper) return;
          swiper.update();
          swiper.slideTo(Math.max(target, 0), 0);
        });
    }
  }

  const init = () => {
    document.querySelectorAll('.main-product__media').forEach(gallery => {
      if (!gallery.querySelector('[data-wb-variant-media]') || gallery.wbVariantMedia) return;
      // Wait for the theme's sliders to start, then filter.
      const start = () => {
        gallery.wbVariantMedia = new WbVariantMedia(gallery);
      };
      if (document.readyState === 'complete') {
        setTimeout(start, 0);
      } else {
        window.addEventListener('load', start, { once: true });
      }
    });
  };

  init();
  document.addEventListener('shopify:section:load', init);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, event => {
      const variant = event?.data?.variant;
      if (!variant) return;
      document.querySelectorAll('.main-product__media').forEach(gallery => {
        gallery.wbVariantMedia?.apply(variant.id);
      });
    });
  }
})();
